import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ArrowRight, Search, User, ChevronDown, Menu, PlayCircle, X } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Logo } from "./Logo";

const NAV = [["Home", "/"], ["Discover", "/discover"], ["Explore", "/explore"], ["Evaluate", "/evaluate"], ["Validate", "/validate"], ["Engage", "/engage"], ["Mobilise", "/mobilise"]];
const MORE = [["Investment Case", "/case"], ["Sources", "/sources"], ["Roles & Admin", "/admin"]];

const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    if (q.trim().length < 2) {
      setRes([]);
      return undefined;
    }
    const t = setTimeout(() => {
      api.get("/search", { params: { q } }).then((r) => setRes(r.data));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative hidden w-[260px] 2xl:block">
      <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
      <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search opportunities, locations, sectors…" className="vig-input !bg-[#06142a]/70 pl-9 !text-[12.5px]" />
      {res.length > 0 && (
        <div data-testid="global-search-results" className="vig-panel absolute left-0 right-0 top-11 z-[1200] max-h-80 overflow-auto p-1 scroll-thin">
          {res.map((r) => (
            <button key={r.kind + r.id} data-testid={`search-result-${r.id}`} onClick={() => { nav(r.route); setQ(""); }} className="vig-row block w-full rounded px-3 py-2 text-left">
              <div className="text-[10px] uppercase tracking-wider text-[#2DD4BF]">{r.kind}</div>
              <div className="text-[13px] text-white">{r.name}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const RoleSwitcher = () => {
  const { users, user, setUserId } = useApp();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button data-testid="role-switcher-btn" onClick={() => setOpen(!open)} className="flex items-center gap-2 rounded-md border border-slate-500/30 px-2.5 py-1.5 hover:border-slate-300/60">
        <User className="h-5 w-5" strokeWidth={1.5} />
        <span className="hidden text-left leading-tight lg:block">
          <span className="block text-[12px] font-semibold text-white" data-testid="current-user-name">{user?.name}</span>
          <span className="block text-[10px] text-[#F4B842]" data-testid="current-user-role">{user?.role_label}</span>
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
      </button>
      {open && (
        <div data-testid="role-switcher-menu" className="vig-panel absolute right-0 top-12 z-[1200] w-80 p-2">
          <div className="px-2 pb-2 pt-1 text-[10.5px] uppercase tracking-wider text-slate-400">Switch demo role — fictional accounts</div>
          {users.map((u) => (
            <button
              key={u.id}
              data-testid={`role-option-${u.role}`}
              onClick={() => { setUserId(u.id); setOpen(false); toast.success(`Now viewing as ${u.role_label}`, { description: u.name }); }}
              className={`vig-row flex w-full items-center justify-between rounded px-2.5 py-2 text-left ${u.id === user?.id ? "bg-white/5" : ""}`}
            >
              <span>
                <span className="block text-[13px] font-semibold text-white">{u.name}</span>
                <span className="block text-[11px] text-slate-400">{u.title}</span>
              </span>
              <span className="rounded bg-[#F4B842]/15 px-1.5 py-0.5 text-[10px] font-bold text-[#F4B842]">{u.role_label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function TopNav() {
  const { tour, setTour } = useApp();
  const [mob, setMob] = useState(false);
  const [more, setMore] = useState(false);
  const nav = useNavigate();
  const startTour = async () => {
    const r = await api.get("/tour");
    setTour({ active: true, step: 0, steps: r.data, startedAt: Date.now() });
  };
  const linkCls = ({ isActive }) => `relative py-2 text-[13.5px] font-medium transition-colors ${isActive ? "text-white after:absolute after:-bottom-1 after:left-0 after:right-0 after:h-[2px] after:bg-[#F4B842]" : "text-slate-200 hover:text-white"}`;
  return (
    <header data-testid="top-nav" className="absolute left-0 right-0 top-0 z-[1100] border-b border-white/5 bg-gradient-to-b from-[#06142a]/90 to-[#06142a]/30 backdrop-blur-[3px]">
      <div className="mx-auto flex h-[74px] max-w-[1600px] items-center gap-5 px-5 lg:px-8">
        <Link to="/" data-testid="nav-logo-link" className="shrink-0"><Logo /></Link>
        <nav className="ml-4 hidden items-center gap-6 xl:flex">
          {NAV.map(([l, t]) => (
            <NavLink key={t} to={t} end={t === "/"} className={linkCls} data-testid={`nav-${l.toLowerCase()}`}>{l}</NavLink>
          ))}
          <div className="relative">
            <button data-testid="nav-more-btn" onClick={() => setMore(!more)} className="flex items-center gap-1 text-[13.5px] text-slate-200 hover:text-white">More <ChevronDown className="h-3.5 w-3.5" /></button>
            {more && (
              <div className="vig-panel absolute left-0 top-9 z-[1200] w-52 p-1" onMouseLeave={() => setMore(false)}>
                {MORE.map(([l, t]) => (
                  <button key={t} data-testid={`nav-more-${t.slice(1)}`} onClick={() => { nav(t); setMore(false); }} className="vig-row block w-full rounded px-3 py-2 text-left text-[13px]">{l}</button>
                ))}
              </div>
            )}
          </div>
        </nav>
        <div className="ml-auto flex items-center gap-3">
          <GlobalSearch />
          {!tour.active && (
            <button data-testid="start-guided-demo-btn" onClick={startTour} className="hidden items-center gap-1.5 rounded-md border border-[#2DD4BF]/50 px-3 py-2 text-[12.5px] font-semibold text-[#5EEAD4] hover:bg-[#2DD4BF]/10 md:flex">
              <PlayCircle className="h-4 w-4" /> Guided Demo
            </button>
          )}
          <Link to="/engage" data-testid="nav-register-interest-btn" className="vig-btn-gold hidden items-center gap-2 px-4 py-2 text-[13px] md:flex">Register Interest <ArrowRight className="h-4 w-4" /></Link>
          <RoleSwitcher />
          <button data-testid="mobile-menu-btn" onClick={() => setMob(!mob)} className="xl:hidden">{mob ? <X /> : <Menu />}</button>
        </div>
      </div>
      {mob && (
        <div data-testid="mobile-menu" className="vig-panel mx-4 mb-3 grid grid-cols-2 gap-1 p-2 xl:hidden">
          {[...NAV, ...MORE].map(([l, t]) => (
            <NavLink key={t} to={t} end={t === "/"} onClick={() => setMob(false)} className="rounded px-3 py-2 text-sm hover:bg-white/5">{l}</NavLink>
          ))}
          <button onClick={() => { startTour(); setMob(false); }} className="rounded px-3 py-2 text-left text-sm text-[#5EEAD4]">Guided Demo</button>
        </div>
      )}
    </header>
  );
}
